import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { extractRange } from "cc-frontend/lib/transferrable-date-utils"
import {
  consolidateCourseDatesOff,
  constructUnitsAndDates,
  findNewDate,
  insertRange,
} from "cc-frontend/lib/unit-utils"
import type { PreparedAction } from "../../../action-doc-builder"

interface Payload {
  course: Course
  startDate: string
  endDate: string
  planbook: Planbook
  rotationCalendar: RotationCalendar
}

export function prepareBumpRangeToDate(payload: Payload): PreparedAction {
  return {
    name: BUMP_RANGE_TO_DATE.name,
    payload: payload,
    patches: BUMP_RANGE_TO_DATE.patches(payload),
    undoPatches: BUMP_RANGE_TO_DATE.undoPatches(payload),
    narrative: BUMP_RANGE_TO_DATE.narrative,
  }
}

export const BUMP_RANGE_TO_DATE = {
  name: "BUMP_RANGE_TO_DATE",

  patches(payload: Payload): Array<PatchModel> {
    let schoolDays = payload.rotationCalendar.attributes.schoolDays

    let consolidatedCourseDatesOff = consolidateCourseDatesOff(
      payload.course,
      payload.planbook,
      payload.rotationCalendar
    )

    let { transferrableArray, courseDatesWithoutRange } = extractRange(
      payload.startDate,
      payload.endDate,
      payload.course.attributes.calendar.dates,
      consolidatedCourseDatesOff,
      schoolDays
    )

    // We want to put it on the day after the end date. E.g. if spring break goes through April 10,
    // we want to insert on April 11
    let newStartDate = findNewDate(1, payload.endDate, consolidatedCourseDatesOff, schoolDays)

    if (newStartDate === undefined) throw Error("No new date found")

    let newCourseDates = insertRange(
      transferrableArray,
      newStartDate,
      1, // this is just to show we're dragging forward. Should be renamed to drgging forward
      null,
      courseDatesWithoutRange,
      consolidatedCourseDatesOff,
      schoolDays,
      false
    )
    let ret = constructUnitsAndDates(newCourseDates, consolidatedCourseDatesOff, schoolDays)
    let { courseDates, units } = ret

    let patch = Patch.create("course", payload.course.id)
      .set(`attributes.calendar.dates`, courseDates)
      .set(`attributes.calendar.units`, units)
      .inc(`attributes.calendar.version`, 1).patch

    return [patch]
  },
  undoPatches(payload: Payload): Array<PatchModel> {
    let patch = Patch.create("course", payload.course.id)
      .set(
        "attributes.calendar.dates",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.dates))
      )
      .set(
        "attributes.calendar.units",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.units))
      )
      .inc("attributes.calendar.version", 1).patch

    return [patch]
  },

  narrative: null,
}
